import { z } from 'zod';
import DOMPurify from 'isomorphic-dompurify';

/**
 * Validation Schemas
 * Reusable Zod schemas for form validation
 */

// Email validation
export const emailSchema = z.string()
  .min(1, 'Email is required')
  .email('Invalid email format')
  .max(255, 'Email must be less than 255 characters');

// Password validation
export const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be less than 128 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');

// Name validation
export const nameSchema = z.string()
  .min(1, 'Name is required')
  .max(100, 'Name must be less than 100 characters')
  .regex(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes');

// Amount validation
export const amountSchema = z.number()
  .min(0, 'Amount must be positive')
  .max(999999999, 'Amount is too large');

// Positive number validation
export const positiveNumberSchema = z.number()
  .positive('Must be a positive number');

// Date validation
export const dateSchema = z.date()
  .refine((date) => date <= new Date(), 'Date cannot be in the future');

// URL validation
export const urlSchema = z.string()
  .url('Invalid URL format')
  .max(2048, 'URL is too long');

// Phone validation
export const phoneSchema = z.string()
  .regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number format');

// Text input validation (for descriptions, notes, etc.)
export const textSchema = z.string()
  .max(1000, 'Text must be less than 1000 characters')
  .optional();

// Required text validation
export const requiredTextSchema = z.string()
  .min(1, 'This field is required')
  .max(1000, 'Text must be less than 1000 characters');

/**
 * XSS Sanitization Utilities
 */

/**
 * Sanitize HTML string to prevent XSS attacks
 * @param {string} dirty - Unsanitized HTML string
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (dirty) => {
  if (!dirty || typeof dirty !== 'string') return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [], // No HTML tags allowed by default
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  });
};

/**
 * Sanitize text input (removes HTML tags and dangerous characters)
 * @param {string} input - User input string
 * @returns {string} - Sanitized string
 */
export const sanitizeText = (input) => {
  if (!input || typeof input !== 'string') return '';
  // Remove HTML tags and dangerous characters
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  }).trim();
};

/**
 * Sanitize number input
 * @param {string|number} input - User input
 * @returns {number|null} - Sanitized number or null
 */
export const sanitizeNumber = (input) => {
  if (typeof input === 'number') {
    return isNaN(input) ? null : input;
  }
  if (typeof input === 'string') {
    const cleaned = input.replace(/[^0-9.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  }
  return null;
};

/**
 * Sanitize email input
 * @param {string} input - Email input
 * @returns {string} - Sanitized email
 */
export const sanitizeEmail = (input) => {
  if (!input || typeof input !== 'string') return '';
  // Remove any HTML tags and dangerous characters, keep only email-safe characters
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  })
    .toLowerCase()
    .trim()
    .replace(/[<>\"'&]/g, ''); // Remove potentially dangerous characters
};

/**
 * Sanitize URL input
 * @param {string} input - URL input
 * @returns {string} - Sanitized URL
 */
export const sanitizeURL = (input) => {
  if (!input || typeof input !== 'string') return '';
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  }).trim();
};

/**
 * Common Validation Schemas
 */

// Login form schema
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required')
});

// Signup form schema
export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
  firstName: nameSchema,
  lastName: nameSchema
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});

// Broker form schema
export const brokerSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema.optional().or(z.literal('')),
  min_roi: z.number().min(0).max(100),
  max_roi: z.number().min(0).max(100)
}).refine((data) => data.max_roi >= data.min_roi, {
  message: "Max ROI must be greater than or equal to Min ROI",
  path: ["max_roi"]
});

// Expense form schema
export const expenseSchema = z.object({
  amount: amountSchema,
  description: requiredTextSchema,
  date: z.string().min(1, 'Date is required')
});

// Payout form schema
export const payoutSchema = z.object({
  user_id: z.string().min(1, 'User is required'),
  role: z.enum(['admin', 'investor'], {
    errorMap: () => ({ message: 'Invalid role' })
  }),
  amount: amountSchema,
  date: z.string().min(1, 'Date is required'),
  admin_name: z.string().min(1, 'Admin name is required').max(100)
});

// Extra Profit form schema
export const extraProfitSchema = z.object({
  transaction_id: z.string().min(1, 'Transaction is required'),
  transaction_amount: amountSchema,
  transaction_type: z.enum(['INR', 'UAE'], {
    errorMap: () => ({ message: 'Transaction type must be INR or UAE' })
  }),
  rates: z.object({
    aed_to_usdt_rate: positiveNumberSchema,
    inr_to_aed_rate: positiveNumberSchema,
    usdt_selling_inr: positiveNumberSchema,
    usdt_buy_rate: positiveNumberSchema,
    usdt_sell_rate: positiveNumberSchema
  })
});

// User form schema
export const userSchema = z.object({
  email: emailSchema,
  firstName: nameSchema,
  lastName: nameSchema,
  role: z.enum(['admin', 'manager', 'staff', 'viewer', 'investor', 'broker', 'trader'], {
    errorMap: () => ({ message: 'Invalid role' })
  }),
  password: passwordSchema.optional().or(z.literal('')),
  invested_amount: z.number().min(0).optional(),
  invested_amount_aed: z.number().min(0).optional(),
  balance_usdt: z.number().min(0).optional(),
  roi_min: z.number().min(0).max(100).optional(),
  roi_max: z.number().min(0).max(100).optional()
}).refine((data) => {
  if (data.roi_min !== undefined && data.roi_max !== undefined) {
    return data.roi_max >= data.roi_min;
  }
  return true;
}, {
  message: "Max ROI must be greater than or equal to Min ROI",
  path: ["roi_max"]
});

// Broker Investor relationship schema
export const brokerInvestorSchema = z.object({
  investor_id: z.string().min(1, 'Investor is required'),
  roi_min: z.number().min(0).max(100),
  roi_max: z.number().min(0).max(100)
}).refine((data) => data.max_roi >= data.min_roi, {
  message: "Max ROI must be greater than or equal to Min ROI",
  path: ["roi_max"]
});

/**
 * Helper function to validate and sanitize input
 * @param {string} input - User input
 * @param {Function} sanitizer - Sanitization function
 * @returns {string} - Sanitized input
 */
export const validateAndSanitize = (input, sanitizer = sanitizeText) => {
  if (!input) return '';
  return sanitizer(input);
};
